import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes } from "react-router-dom";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { customerSettingsQuery } from "@/api/customerSettings";
import { useAuth } from "@/stores/auth";
import { useUi } from "@/stores/ui";
import { applyTheme } from "@/theme/applyTheme";
import { Login } from "@/pages/Login";
import { LoginAssistPage } from "@/pages/LoginAssistPage";
import { SetPasswordPage } from "@/pages/SetPasswordPage";
import { ConnectTicketPage } from "@/pages/ConnectTicketPage";
import { isBookingPath, startBookingLoad } from "@/api/bookingPublic";
import { ConfirmProvider } from "@/components/ui/ConfirmModal";
import { RootBoundary } from "@/components/ui/ErrorBoundary";
import { EnvBanner } from "@/components/layout/EnvBanner";
import { Toaster, toast } from "@/components/ui/Toast";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError, errorText } from "@/lib/api";
import { AdminApp, RouteLoading } from "@/routes/pages";
import { companionRoutes, desktopRoutes, familyRoutes } from "@/routes/customerRoutes";
import { AppOpenRoute, agentDevRoutes } from "@/routes/appsPages";
import { isMacShell } from "@/native/desktop";
import { DesktopHistoryBridge } from "@/native/DesktopBridge";
import { isEmbedded, showsOffline } from "@/native/embedded";
import { EmbeddedOffline } from "@/native/EmbeddedOffline";
import { NativeLinkBridge } from "@/native/NativeLinkBridge";
import { useAppearanceSync } from "@/features/appearance/useAppearanceSync";
import { RecorderLifecycle, RecorderProvider, RecordingSignedOutBar } from "@/features/meetings/recorder/RecorderRoot";

// Auth gate + providers. The route tables live in src/routes/
// (customerRoutes.tsx); page modules are lazy-loaded there (pages.tsx).
// Kept eager: Login (always shown when signed out), the shells (chrome
// present on every signed-in route), the auth store and the query client.

// Two things happen for every failed call, in one place:
// - a 401 from anything but /api/auth/* asks whether the session still
//   holds (checkSession: only a 401 from /api/auth/me signs out);
// - a failed mutation nobody handles becomes one toast. Not when the
//   mutation says it shows its own error (meta.errorHandled, or its own
//   onError), not for an abort, and not for 400/401/409/422, which forms
//   and confirm flows show inline.

function askIfSessionEnded(error: unknown) {
  // Not before the boot check has answered: a read started beside it (the
  // settings prefetch) that meets an expired cookie must not make the
  // login page say the session ended; the boot check signs out by itself.
  if (!useAuth.getState().hydrated) return;
  if (error instanceof ApiError && error.status === 401 && !error.path?.startsWith("/api/auth/")) {
    void useAuth.getState().checkSession();
  }
}

const SHOWN_INLINE = new Set([400, 401, 409, 422]);

// The boot prefetch of the settings (below) keeps the default single retry:
// the shell's settings readers join that fetch and inherit its retry, so a
// passing 502 or a dropped connection must not leave the nav without its
// gated rows. A refusal is final: an expired cookie (the boot check signs
// out by itself) or a previous login's workspace.
function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
  return failureCount < 1;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: askIfSessionEnded }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      askIfSessionEnded(error);
      if (mutation.meta?.errorHandled === true || mutation.options.onError) return;
      if (error instanceof Error && error.name === "AbortError") return;
      if (error instanceof ApiError && SHOWN_INLINE.has(error.status)) return;
      toast.error(errorText(error));
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5_000,
    },
  },
});

// The public booking page (/book/<slug>, /book/manage): its own chunk.
const BookingPage = lazy(() => import("@/pages/BookingPage"));
// The self-serve sign-up (/signup, features/signup): its own chunk too.
const SignupPage = lazy(() => import("@/pages/SignupPage"));

/** The public page's frame while its chunk loads: a skeleton, no words. */
function BookingLoading() {
  return (
    <div className="flex app-vh w-full justify-center bg-surface-950" aria-busy="true">
      <div className="w-full max-w-[480px] px-4 pt-6">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-3 h-7 w-48" />
        <Skeleton className="mt-8 h-[60px] w-full" />
      </div>
    </div>
  );
}

const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

function AuthenticatedApp() {
  const authenticated = useAuth((s) => s.authenticated);
  const customerId = useAuth((s) => s.customerId);
  const role = useAuth((s) => s.role);
  const hydrated = useAuth((s) => s.hydrated);
  const rehydrate = useAuth((s) => s.rehydrate);
  const signedOut = useAuth((s) => s.signedOut);

  // Rehydrate the session from the HttpOnly cookie on boot.
  // /api/auth/me is the only call allowed without auth — it
  // returns 200 with the decoded session or 401 if the
  // cookie is missing / expired. Either outcome flips
  // `hydrated` so the gate below unblocks.
  //
  // The workspace's settings (which nav rows it has) are asked for at the
  // same time, for the workspace this browser last signed in to: the shell
  // then mounts with them, one round trip earlier. A previous login's
  // workspace or an expired cookie costs one refused read, nothing more.
  useEffect(() => {
    const { customerId: lastCustomer, role: lastRole } = useAuth.getState();
    if (lastCustomer && lastRole === "customer") {
      void queryClient.prefetchQuery({ ...customerSettingsQuery(lastCustomer), retry: retryUnlessRefused });
    }
    void rehydrate();
  }, [rehydrate]);

  // Apply the colour theme to <html data-theme>. The inline script in
  // index.html sets the same attribute before first paint from the
  // persisted store; this keeps it in sync once React owns it and
  // re-applies instantly when the user flips themes in Settings. Runs
  // for every render branch below (login / admin / customer) because
  // it's declared above the early returns.
  const theme = useUi((s) => s.theme);
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // One theme per login across the web, the iPhone app and the Mac app:
  // read after sign-in, on return to the window and every 5 minutes; a
  // choice here is sent. Inside the iPhone app the app does it natively.
  const user = useAuth((s) => s.user);
  useAppearanceSync(authenticated ? user : null, !isEmbedded());

  // Until rehydrate resolves we don't know whether the user
  // is authenticated — the persisted localStorage flag is a
  // display hint only. Render a neutral splash instead of
  // flashing Login → app (or app → Login if the cookie
  // actually expired).
  if (!hydrated) {
    // Inside the iPhone app its own placeholder covers the page until it
    // has drawn: a splash here would only flash between the two.
    if (isEmbedded()) return <div className="h-full w-full" aria-busy="true" />;
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-surface-950" role="status">
        <span className="text-sm text-fg-subtle">Loading…</span>
      </div>
    );
  }

  if (!authenticated) {
    // Inside the iPhone app, a check that never reached the server is not a
    // sign-out: say so and retry, rather than show a login form.
    if (showsOffline(isEmbedded(), signedOut)) return <EmbeddedOffline />;
    return <Login />;
  }

  // An app's sign-in hand-off (apps spec §8.3): the gate sends a browser
  // with no app cookie here, and Login above answered it if signed out
  // (the URL stays). For every signed-in role, before the role shells: a
  // centred page with no shell. Two segments: the edge 302s /agent-dev/open.
  if (/^\/agent-dev\/open\/app\/?$/.test(window.location.pathname)) {
    return (
      <QueryClientProvider client={queryClient}>
        <ConfirmProvider>
          <BrowserRouter future={routerFuture}>
            <AppOpenRoute />
          </BrowserRouter>
        </ConfirmProvider>
      </QueryClientProvider>
    );
  }

  // Admin role lands in a separate shell with zero customer-shaped
  // UI — sidebar, routes, and data are platform-wide. Admins who
  // need to look at a specific customer's workspace can switch
  // (not yet built; for now the admin sits at /admin permanently).
  if (role === "admin") {
    return (
      <QueryClientProvider client={queryClient}>
        <ConfirmProvider>
          <BrowserRouter future={routerFuture}>
            <Suspense fallback={<RouteLoading />}>
              <AdminApp />
            </Suspense>
          </BrowserRouter>
        </ConfirmProvider>
      </QueryClientProvider>
    );
  }

  // A child (family role) gets its own shell: one conversation with its
  // own helper and nothing else. The server already refuses every
  // operator route for this role — this is the visible half of that, so
  // there is nowhere in the UI to go either.
  if (role === "family") {
    if (!customerId) return <Login />;
    return (
      <QueryClientProvider client={queryClient}>
        <ConfirmProvider>
          <BrowserRouter future={routerFuture}>
            {/* Above the routes: a meeting recording outlives every navigation (features/meetings/recorder). */}
            <RecorderProvider customerId={customerId}>
              <Routes>{familyRoutes(customerId)}</Routes>
            </RecorderProvider>
          </BrowserRouter>
        </ConfirmProvider>
      </QueryClientProvider>
    );
  }

  if (!customerId) {
    return <Login />;
  }

  // Customer users land directly in their workspace. No tenant
  // selector, no global overview. The sidebar is customer-scoped.
  return (
    <QueryClientProvider client={queryClient}>
      <ConfirmProvider>
        <BrowserRouter future={routerFuture}>
          {/* Above the routes: a meeting recording outlives every navigation (features/meetings/recorder). */}
          <RecorderProvider customerId={customerId}>
            <Routes>
              {/* Phones land in the companion (LandingRedirect and the shell's
                  own redirect); the full dashboard is a tap away. */}
              {companionRoutes(customerId)}
              {agentDevRoutes()}
              {desktopRoutes(customerId)}
            </Routes>
          </RecorderProvider>
          {/* A web screen inside the iPhone app hands a navigation to one
              of the app's own screens over to it (src/native/linkBridge.ts). */}
          {isEmbedded() && <NativeLinkBridge />}
          {/* The Mac app's Back / Forward (src/native/desktop.ts). */}
          {isMacShell() && <DesktopHistoryBridge />}
        </BrowserRouter>
      </ConfirmProvider>
    </QueryClientProvider>
  );
}

export function App() {
  // Standalone, unauthenticated surface — opened cold from a Telegram
  // link, no lattice cookie at all. Must resolve BEFORE the auth-gated
  // AuthenticatedApp (which would otherwise bounce straight to <Login/>).
  // Checked via window.location rather than a react-router route: no
  // <BrowserRouter> exists at this level — every route lives inside
  // AuthenticatedApp's own auth-gated branches. Kept as its own
  // zero-hooks wrapper (rather than an early return inside
  // AuthenticatedApp, before its hooks) so rules-of-hooks stays clean.
  if (window.location.pathname.startsWith("/login-assist/")) {
    return <LoginAssistPage />;
  }
  // The same for a set-password link an admin sent (features/auth): its
  // token is in the fragment, for this page only, signed in or not.
  if (window.location.pathname === "/set-password" || window.location.pathname.startsWith("/set-password/")) {
    return <SetPasswordPage />;
  }
  // And for the sign-in link an agent sends to connect a product for it
  // (features/connections): its ticket is in the fragment. The whole
  // segment only: /connections stays a page inside the signed-in app.
  if (window.location.pathname === "/connect" || window.location.pathname.startsWith("/connect/")) {
    return <ConnectTicketPage />;
  }
  // A person asking for a workspace with Google (features/signup): no session.
  if (window.location.pathname === "/signup" || window.location.pathname.startsWith("/signup/")) {
    return (
      <Suspense fallback={<BookingLoading />}>
        <SignupPage />
      </Suspense>
    );
  }
  // A scheduling link a stranger books time on (features/booking-pages/public):
  // no session, no toaster, no banner. The page's GET starts now, beside its
  // chunk, so the times arrive one round trip sooner (C15).
  if (isBookingPath(window.location.pathname)) {
    startBookingLoad(window.location.pathname);
    return (
      <Suspense fallback={<BookingLoading />}>
        <BookingPage />
      </Suspense>
    );
  }
  // A non-prod environment (dev) says so on every page, signed in or not.
  // The toasts too: every role and the login page share the one Toaster.
  return (
    <>
      <EnvBanner />
      <Toaster />
      {/* A recording goes on through a sign-out: its guards live here, and behind the sign-in page it says so. */}
      <RecorderLifecycle />
      <RecordingSignedOutBar />
      {/* A crash in the app replaces only the app: the recorder's guards above stay. */}
      <RootBoundary>
        <AuthenticatedApp />
      </RootBoundary>
    </>
  );
}
